import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Cog, ShieldCheck, Gem, Droplets, Star } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { useApp } from "@/context/AppContext";
import { useReveal } from "@/hooks/useReveal";

const HERO_BG = "https://images.pexels.com/photos/8991530/pexels-photo-8991530.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const STORY_IMG = "https://images.pexels.com/photos/8327681/pexels-photo-8327681.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const HERO_WATCH = "/watches/hero-chronograph-t.png";

const Home = () => {
  const { t } = useApp();
  const ref = useReveal();
  const advantageIcons = [Cog, ShieldCheck, Gem, Droplets];

  return (
    <main ref={ref} data-testid="home-page">

      {/* ============ HERO ============ */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={HERO_BG} alt="" className="hero-bg w-full h-full object-cover opacity-90" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#170F09]/95 via-[#170F09]/60 to-[#170F09]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#170F09] via-transparent to-[#170F09]/20" />
        </div>

        {/* Vertical brand tag running along the left edge */}
        <div
          className="hidden lg:block absolute left-8 top-1/2 z-10 -translate-y-1/2 font-mono text-[0.62rem] tracking-[0.5em] text-white/35 uppercase whitespace-nowrap"
          style={{ writingMode: "vertical-rl", transform: "translateY(-50%) rotate(180deg)" }}
        >
          SOMMET ZÜRICH — Réf. No. 001 — Swiss Made
        </div>

        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-14 lg:pl-24 pt-28 pb-40 w-full">
          {/* Oversized overlapping headline + watch */}
          <div className="relative grid lg:grid-cols-12 items-center">
            <div className="reveal lg:col-span-8 relative z-20">
              <div className="kicker kicker-eyebrow mb-6">{t.hero.kicker}</div>
              <h1 className="section-heading text-[3.2rem] sm:text-7xl lg:text-8xl xl:text-[7.5rem] font-medium text-white leading-[0.92] -ml-1">
                BUILD<br />YOUR <span className="text-[#C9A36B]">SOMMET</span>
              </h1>
            </div>

            <div className="reveal lg:col-span-5 lg:-ml-[10%] flex justify-center lg:justify-end relative z-10 -mt-6 lg:mt-0">
              <div className="relative corner-frame p-8 md:p-12">
                <span className="corner-frame-tr" />
                <span className="corner-frame-bl" />
                <div className="absolute inset-0 blur-3xl bg-black/40 scale-90" />
                <img
                  src={HERO_WATCH}
                  alt="SOMMET watch"
                  className="relative w-full max-w-[480px] drop-shadow-[0_40px_60px_rgba(0,0,0,0.7)]"
                  data-testid="hero-watch-img"
                />
              </div>
            </div>
          </div>

          <div className="reveal max-w-md mt-10 lg:mt-4 relative z-20">
            <p className="text-white/65 text-base md:text-lg leading-relaxed mb-10">
              {t.hero.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link data-testid="hero-configure-cta" to="/configurator" className="btn-champagne">
                {t.hero.cta} <ChevronRight size={16} />
              </Link>
              <Link data-testid="hero-collection-cta" to="/collection" className="btn-ghost">
                {t.hero.cta2}
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 z-10 bg-[#170F09]/85 backdrop-blur-md border-t border-white/5">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
            {t.advantages.items.map((item, i) => {
              const Icon = advantageIcons[i];
              return (
                <div key={i} data-testid={`hero-advantage-${i}`} className="flex items-center gap-3">
                  <Icon size={20} strokeWidth={1.2} className="text-[#C9A36B] shrink-0" />
                  <div>
                    <div className="font-mono text-[0.65rem] tracking-[0.2em] uppercase text-white">{item.title}</div>
                    <div className="text-xs text-white/50">{item.desc.split('—')[0].trim()}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ STORY ============ */}
      <section data-testid="story-section" className="py-28 md:py-40 bg-[#170F09]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <div className="reveal">
            <img src={STORY_IMG} alt="Watchmaker" className="w-full h-[560px] object-cover grayscale hover:grayscale-0 transition-all duration-1000" />
          </div>
          <div className="reveal">
            <div className="kicker kicker-eyebrow mb-4">{t.story.kicker}</div>
            <h2 className="section-heading text-4xl md:text-5xl lg:text-6xl mb-8">{t.story.title}</h2>
            <p className="text-white/65 leading-relaxed mb-6">{t.story.p1}</p>
            <p className="text-white/65 leading-relaxed mb-12">{t.story.p2}</p>
            <div className="grid grid-cols-3 gap-6 border-t border-white/10 pt-8">
              <Stat label={t.story.stat1Label} value={t.story.stat1Value} />
              <Stat label={t.story.stat2Label} value={t.story.stat2Value} />
              <Stat label={t.story.stat3Label} value={t.story.stat3Value} />
            </div>
          </div>
        </div>
      </section>

      {/* ============ SWISS QUALITY ============ */}
      <section
        data-testid="quality-section"
        className="relative py-28 md:py-40"
        style={{
          background: "radial-gradient(ellipse 100% 60% at 50% 0%, #2C1E10 0%, #21160B 60%)",
          clipPath: "polygon(0 56px, 100% 0, 100% 100%, 0 100%)",
          marginTop: "-56px",
        }}
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 pt-8">
          <div className="max-w-3xl mb-16 reveal">
            <div className="kicker kicker-eyebrow mb-4">{t.quality.kicker}</div>
            <h2 className="section-heading text-4xl md:text-5xl lg:text-6xl">{t.quality.title}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {t.quality.items.map((it, i) => (
              <div key={i} data-testid={`quality-item-${i}`} className="card-lift border border-white/8 bg-white/[0.015] p-10 reveal">
                <div className="font-mono text-3xl text-[#C9A36B]/40 mb-8">0{i + 1}</div>
                <div className="font-heading text-2xl mb-3">{it.title}</div>
                <p className="text-white/55 leading-relaxed">{it.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ REVIEWS ============ */}
      <section data-testid="reviews-section" className="py-28 md:py-40 bg-[#170F09]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="text-center mb-16 reveal">
            <div className="kicker kicker-eyebrow mb-4">{t.reviews.kicker}</div>
            <h2 className="section-heading text-4xl md:text-5xl lg:text-6xl">{t.reviews.title}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {t.reviews.items.map((r, i) => (
              <div key={i} data-testid={`review-${i}`} className="card-lift bg-[#21160B] border border-white/8 p-10 reveal">
                <div className="flex gap-1 mb-8">
                  {Array.from({ length: 5 }).map((_, k) => (
                    <Star key={k} size={12} fill="#C9A36B" strokeWidth={0} />
                  ))}
                </div>
                <p className="text-white/80 leading-relaxed mb-8 italic font-heading text-lg">&laquo;{r.text}&raquo;</p>
                <div className="border-t border-white/10 pt-4">
                  <div className="font-medium">{r.name}</div>
                  <div className="kicker mt-1">{r.city}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-xs mx-auto px-6 py-10 bg-[#170F09]">
        <div className="divider-ornament"><span className="diamond" /></div>
      </div>

      {/* ============ FAQ ============ */}
      <section data-testid="faq-section" className="py-20 md:py-32 bg-[#170F09]">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <div className="text-center mb-14 reveal">
            <div className="kicker kicker-eyebrow mb-4">{t.faq.kicker}</div>
            <h2 className="section-heading text-4xl md:text-5xl">{t.faq.title}</h2>
          </div>
          <div className="reveal">
            <Accordion type="single" collapsible className="w-full">
              {t.faq.items.map((item, i) => (
                <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`} className="border-white/10">
                  <AccordionTrigger className="text-left font-heading text-lg hover:no-underline hover:text-[#C9A36B] py-6">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-white/60 pb-6 leading-relaxed">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

    </main>
  );
};

const Stat = ({ label, value }) => (
  <div>
    <div className="font-heading text-4xl md:text-5xl text-[#C9A36B]">{value}</div>
    <div className="kicker mt-2">{label}</div>
  </div>
);

export default Home;
